import { useToast } from '@lifebit-ai/lds'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useToggleState } from 'hooks/useToggleState'
import { getExportRequestName, isDataAccessRequest } from 'modules/requests/utils'
import { useGetImportRequestVerification } from 'services/import-requests/hooks/useGetImportRequestVerification'
import { isUploadedImportRequestDetail } from 'services/import-requests/importRequestUtils'
import { QueryKeys } from 'services/queryKeys'
import { type Result, ResultRequestType } from 'services/results/types'
import { getAdditionalInfoRequestContextPrefix } from 'utils/additionalInfoFormCopy'
import { copyTextToClipboard } from 'utils/string'

import { formatCloudStorageUri, getCopyCloudStorageLinkTooltip, getFormTypeLabel, isDifferentRequester } from '../utils'

interface UseResultMetaParams {
  result: Result
  isFile: boolean
  bucket: string
  path: string
  contents: (S3FileAsContent | S3DirAsContent)[]
  isReadyForDownload: boolean
  resultId: string
}

const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024
  return kb < 1 ? `${bytes} B` : `${kb.toFixed(1)} KB`
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${datePart} at ${timePart}`
}

const getRequestContextPrefix = (requestType: ResultRequestType): string => {
  if (requestType === ResultRequestType.AppealRequest) return 'This is an appeal request. '
  if (requestType === ResultRequestType.Attestation) return getAdditionalInfoRequestContextPrefix()

  return ''
}

const getFileKey = (path: string, name: string | undefined, isFile: boolean): string =>
  isFile ? path : `${path}/${name ?? ''}`

const getResultMetaDisplayName = (result: Result, isFile: boolean, path: string): string => {
  if (isFile) {
    const pathBasename = path.split('/').filter(Boolean).pop() ?? ''

    if (pathBasename) {
      return pathBasename
    }

    const resultName = result.name?.trim()

    if (resultName) {
      return resultName
    }
  } else {
    const resultName = result.name?.trim()

    if (resultName) {
      return resultName
    }
  }

  const fallbackName = getExportRequestName(result)

  return fallbackName === '--' ? '' : fallbackName
}

const useResultMeta = ({
  result,
  isFile,
  bucket,
  path,
  contents,
  isReadyForDownload,
  resultId
}: UseResultMetaParams) => {
  const { name, fileChangeStatus } = result
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedFileToPreview, setSelectedFileToPreview] = useState<S3FileAsContent | undefined>()
  const [isViewFormModalOpen, toggleViewFormModal] = useToggleState(false)

  const isImportRequest = result.requestType === ResultRequestType.ImportRequest
  const { data: importRequest, isLoading: isImportRequestLoading } = useGetImportRequestVerification({
    requestId: result._id,
    options: { enabled: isImportRequest }
  })

  const displayName = getResultMetaDisplayName(result, isFile, path)
  const file = contents.find((item): item is S3FileAsContent => !item.isDir)
  const isFileChanged = fileChangeStatus === 'changed'
  const requestContextPrefix = getRequestContextPrefix(result.requestType)

  const fileChangedAlertMessage = `${requestContextPrefix}The file content has changed since the original approval.`

  const differentRequesterAlertMessage = `${requestContextPrefix}The user requesting this export differs from the user on the original approved request.`

  const hasDifferentRequester = isDifferentRequester(result)
  const isDataAccessRequestResult = isDataAccessRequest(result)
  const fileKey = getFileKey(path, name, isFile)
  const copyLinkTooltip = getCopyCloudStorageLinkTooltip(result.beforeCheckLocation)
  const isAutomatedChecksToReRunVisible = result.automatedChecks?.mode === 'ran'
  const isAutomatedChecksSkippedVisible = result.automatedChecks?.mode === 'skipped'

  const onCopyS3Link = () => {
    copyTextToClipboard(formatCloudStorageUri(result.beforeCheckLocation, fileKey))
    toast({ message: 'File link copied to clipboard', type: 'success' })
  }

  const onPreviewFile = () => {
    if (file) setSelectedFileToPreview(file)
  }

  const onClosePreview = () => {
    queryClient.cancelQueries({ queryKey: QueryKeys.GetFileContentsFromSignedUrl() })
    setSelectedFileToPreview(undefined)
  }

  const previewFileKey = selectedFileToPreview ? getFileKey(path, selectedFileToPreview.name, isFile) : ''

  const formattedFileSize = file ? formatFileSize(file.size) : ''
  const formattedLastModified = file ? formatDate(file.lastModified) : ''

  const uploadedImportRequest =
    importRequest && isUploadedImportRequestDetail(importRequest) ? importRequest : undefined

  const importRequestFileName = uploadedImportRequest?.fileName
  const importRequestFileSize =
    uploadedImportRequest?.sizeInBytes !== undefined ? formatFileSize(uploadedImportRequest.sizeInBytes) : undefined

  const importRequestScanResult = uploadedImportRequest?.scanResult
  const importRequestScanStartedAt = importRequest?.scanStartedAt
  const importRequestScanCompletedAt = importRequest?.scanCompletedAt
  const importRequestScanStatusCode = uploadedImportRequest?.scanStatusCode
  const participantFile = result.dataAccessRequest?.participantFile
  const customFormSubmission = result.formSubmission ?? result.appealReview?.previousRequest.formSubmission
  const customFormTypeLabel = customFormSubmission ? getFormTypeLabel(customFormSubmission.type) : undefined
  const downloadDetails = isReadyForDownload
    ? {
        bucket,
        resultId,
        fileKey,
        fileName: displayName ?? ''
      }
    : undefined

  return {
    displayName,
    file,
    isFileChanged,
    fileChangedAlertMessage,
    isDifferentRequester: hasDifferentRequester,
    differentRequesterAlertMessage,
    isAutomatedChecksToReRunVisible,
    isAutomatedChecksSkippedVisible,
    fileKey,
    formattedFileSize,
    formattedLastModified,
    selectedFileToPreview,
    previewFileKey,
    isImportRequest,
    importRequestFileName,
    importRequestFileSize,
    importRequestScanResult,
    importRequestScanStartedAt,
    importRequestScanCompletedAt,
    importRequestScanStatusCode,
    isImportRequestScanLoading: isImportRequestLoading,
    customFormSubmission,
    customFormTypeLabel,
    isDataAccessRequestResult,
    participantFile,
    downloadDetails,
    copyLinkTooltip,
    isViewFormModalOpen,
    toggleViewFormModal,
    onCopyS3Link,
    onPreviewFile,
    onClosePreview
  }
}

export default useResultMeta
